// Case study metadata interface
export interface CaseStudyMeta {
  slug: string;
  title: string;
  subtitle: string;
  youtube_id: string;
  client: string;
  image: string;
}

// Full case study with content
export interface CaseStudy extends CaseStudyMeta {
  content: string;
}

// Import all markdown files from subfolders
const caseStudyModules = import.meta.glob('/content/casestudies/*/index.md', {
  eager: true,
  query: '?raw',
  import: 'default',
});

// Parse frontmatter and content from markdown
function parseCaseStudy(markdown: string): CaseStudy {
  // Simple frontmatter parser
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
  const match = markdown.match(frontmatterRegex);

  if (!match) {
    throw new Error('Invalid markdown format');
  }

  const [, frontmatterStr, content] = match;

  // Parse frontmatter
  const frontmatter: Record<string, string> = {};
  frontmatterStr.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim().replace(/^["']|["']$/g, '');
      frontmatter[key] = value;
    }
  });

  return {
    slug: frontmatter.slug,
    title: frontmatter.title,
    subtitle: frontmatter.subtitle,
    youtube_id: frontmatter.youtube_id,
    client: frontmatter.client,
    image: frontmatter.image,
    content: content.trim(),
  };
}

// Get all case studies
export function getAllCaseStudies(): CaseStudy[] {
  const caseStudies: CaseStudy[] = [];

  for (const [path, module] of Object.entries(caseStudyModules)) {
    try {
      const markdown = module as string;
      const caseStudy = parseCaseStudy(markdown);

      // Convert relative image path to absolute path based on slug
      if (caseStudy.image && caseStudy.image.startsWith('./')) {
        caseStudy.image = `/content/casestudies/${caseStudy.slug}/${caseStudy.image.substring(2)}`;
      }

      caseStudies.push(caseStudy);
    } catch (error) {
      console.error(`Error parsing case study at ${path}:`, error);
    }
  }

  return caseStudies;
}

// Get a single case study by slug
export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  const allCaseStudies = getAllCaseStudies();
  return allCaseStudies.find(cs => cs.slug === slug);
}

// Get case study metadata only (without content)
export function getAllCaseStudiesMeta(): CaseStudyMeta[] {
  return getAllCaseStudies().map(({ slug, title, subtitle, youtube_id, client, image }) => ({
    slug,
    title,
    subtitle,
    youtube_id,
    client,
    image,
  }));
}
